"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = tslib_1.__importStar(require("react"));
const react_dom_1 = tslib_1.__importDefault(require("react-dom"));
const prop_types_1 = tslib_1.__importDefault(require("prop-types"));
const react_popper_1 = require("react-popper");
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const isNil_1 = tslib_1.__importDefault(require("lodash/fp/isNil"));
const noop_1 = tslib_1.__importDefault(require("lodash/fp/noop"));
const logDeprecatedWarnings_1 = tslib_1.__importDefault(require("../../utils/logDeprecatedWarnings"));
const portalRoot_1 = require("../../utils/portalRoot");
const useClickOutside_1 = tslib_1.__importDefault(require("../../hooks/useClickOutside"));
const getMenuItems_1 = tslib_1.__importDefault(require("../menuItems/getMenuItems"));
const MenuItems_1 = tslib_1.__importDefault(require("../menuItems/MenuItems"));
const menuItemsPropTypes_1 = tslib_1.__importDefault(require("./menuItemsPropTypes"));
const DropdownToggleButton_1 = tslib_1.__importDefault(require("./DropdownToggleButton"));
const SplitCaretButton_1 = tslib_1.__importDefault(require("./SplitCaretButton"));
const Caret_1 = tslib_1.__importDefault(require("./Caret"));
const getPlacement = (pullRight, dropup) => {
    if (pullRight && dropup) {
        return 'top-end';
    }
    if (!pullRight && dropup) {
        return 'top-start';
    }
    if (pullRight && !dropup) {
        return 'bottom-end';
    }
    return 'bottom-start';
};
const ButtonDropdown = props => {
    const { id, items, bsSize, bsStyle, disabled, iconOnly, title, splitButton, customDropdown, toggleClassName, dropdownClassName, className, open, dropup, pullRight, noCaret, onOpen, onClose, onLabelButtonClick, usePortal, popperConfig } = props, remainingProps = tslib_1.__rest(props, ["id", "items", "bsSize", "bsStyle", "disabled", "iconOnly", "title", "splitButton", "customDropdown", "toggleClassName", "dropdownClassName", "className", "open", "dropup", "pullRight", "noCaret", "onOpen", "onClose", "onLabelButtonClick", "usePortal", "popperConfig"]);
    const [internalOpen, setInternalOpen] = (0, react_1.useState)(open);
    const [refDropdownToggle, setRefDropdownToggle] = react_1.default.useState(null);
    const [refDropdownMenu, setRefDropdownMenu] = react_1.default.useState(null);
    const [refSplitButtonToggle, setRefSplitButtonToggle] = react_1.default.useState(null);
    const defaultPopperConfig = {
        placement: getPlacement(pullRight, dropup),
        modifiers: [],
    };
    const popperParentRef = splitButton && pullRight ? refSplitButtonToggle : refDropdownToggle;
    const { styles, attributes } = (0, react_popper_1.usePopper)(popperParentRef, refDropdownMenu, popperConfig || defaultPopperConfig);
    const wrapperRef = (0, useClickOutside_1.default)(() => closeMenu());
    const dropdownRoot = (0, portalRoot_1.getOrCreatePortalRoot)();
    const isUncontrolled = (0, isNil_1.default)(open);
    const shouldShowCaret = !noCaret && !splitButton && !iconOnly;
    (0, react_1.useEffect)(() => {
        if (!isUncontrolled) {
            // console.log('update open from outside: ' + open);
            setInternalOpen(open);
        }
    }, [isUncontrolled, open]);
    const toggleOpen = event => {
        const isOpen = isUncontrolled ? internalOpen : open;
        if (isOpen) {
            closeMenu();
        }
        else {
            openMenu(event);
        }
    };
    const openMenu = event => {
        if (isUncontrolled) {
            setInternalOpen(true);
        }
        onOpen(event);
    };
    const closeMenu = () => {
        if (isUncontrolled) {
            setInternalOpen(false);
        }
        onClose();
    };
    const handleSplitLabelButtonClick = () => {
        closeMenu();
        onLabelButtonClick();
    };
    const handleDropdownButtonClick = splitButton ? handleSplitLabelButtonClick : toggleOpen;
    const isOpen = isUncontrolled ? internalOpen : open;
    const wrapperClasses = (0, classnames_1.default)('dropdown', 'btn-group', isOpen && 'open', className);
    const dropdownClasses = (0, classnames_1.default)(usePortal && 'dropdown-portal', splitButton && pullRight && 'pull-right', dropdownClassName);
    const dropdownMenu = ((0, jsx_runtime_1.jsx)(MenuItems_1.default, Object.assign({ className: dropdownClasses, ref: setRefDropdownMenu, style: styles.popper }, attributes.popper, { children: customDropdown ? customDropdown : (0, getMenuItems_1.default)(items, toggleOpen) })));
    return ((0, jsx_runtime_1.jsxs)("div", Object.assign({}, remainingProps, { className: wrapperClasses, ref: wrapperRef }, { children: [(0, jsx_runtime_1.jsx)(DropdownToggleButton_1.default, Object.assign({ id: id, splitButton: splitButton, bsStyle: bsStyle, bsSize: bsSize, iconOnly: iconOnly, disabled: disabled, ref: setRefDropdownToggle, onClick: handleDropdownButtonClick, className: toggleClassName }, { children: (0, jsx_runtime_1.jsxs)(react_1.default.Fragment, { children: [title, shouldShowCaret && (0, jsx_runtime_1.jsx)(Caret_1.default, {})] }) })), splitButton && ((0, jsx_runtime_1.jsx)(SplitCaretButton_1.default, { id: id, bsStyle: bsStyle, disabled: disabled, className: toggleClassName, onClick: toggleOpen, ref: setRefSplitButtonToggle })), isOpen && usePortal && react_dom_1.default.createPortal(dropdownMenu, dropdownRoot), isOpen && !usePortal && dropdownMenu] })));
};
ButtonDropdown.defaultProps = {
    id: Math.random().toString(36).substr(2, 16),
    items: [],
    iconOnly: false,
    noCaret: false,
    pullRight: false,
    splitButton: false,
    dropup: false,
    bsStyle: 'default',
    disabled: false,
    onOpen: noop_1.default,
    onClose: noop_1.default,
    onLabelButtonClick: noop_1.default,
    toggleClassName: '',
    usePortal: false,
    className: '',
};
ButtonDropdown.propTypes = {
    id: prop_types_1.default.string,
    title: prop_types_1.default.oneOfType([prop_types_1.default.node, prop_types_1.default.string]).isRequired,
    open: prop_types_1.default.bool,
    dropup: prop_types_1.default.bool,
    pullRight: prop_types_1.default.bool,
    bsSize: prop_types_1.default.oneOf(['xs', 'sm', 'lg']),
    bsStyle: prop_types_1.default.oneOf(['default', 'primary', 'info', 'warning', 'danger', 'success', 'link', 'muted']),
    iconOnly: prop_types_1.default.bool,
    noCaret: prop_types_1.default.bool,
    splitButton: prop_types_1.default.bool,
    usePortal: prop_types_1.default.bool,
    items: menuItemsPropTypes_1.default.isRequired,
    disabled: prop_types_1.default.bool,
    className: prop_types_1.default.string,
    dropdownClassName: prop_types_1.default.string,
    toggleClassName: prop_types_1.default.string,
    onLabelButtonClick: prop_types_1.default.func,
    customDropdown: prop_types_1.default.node,
    popperConfig: prop_types_1.default.object,
    enableOnClickOutside: prop_types_1.default.func,
    disableOnClickOutside: prop_types_1.default.func,
    onOpen: prop_types_1.default.func,
    onClose: prop_types_1.default.func,
};
exports.default = ButtonDropdown;
